import React, { useId } from 'react';
import { getLeftInletY } from './pipePorts';

export interface TankMixerState {
  isAttached?: boolean | undefined;
  isRunning?: boolean | undefined;
  speed?: number | undefined;
  color?: string | undefined;
  bladeType?: string | undefined;
}

export interface TankLevelSensorState {
  isAttached?: boolean | undefined;
  levelPercent?: number | undefined;
}

export interface TankSvgProps {
  levelPercent: number; // 0 to 100
  capacity?: number | undefined; // e.g. 1000 Liters
  liquidColor?: string | undefined; // custom liquid color
  inletCount?: number | undefined; // 1 to 4 inlets
  mixerState?: TankMixerState | undefined;
  levelSensorState?: TankLevelSensorState | undefined;
  showScale?: boolean | undefined;
  showDigital?: boolean | undefined;
  className?: string | undefined;
  style?: React.CSSProperties | undefined;
}

export const TankSvg: React.FC<TankSvgProps> = ({
  levelPercent,
  capacity = 1000,
  liquidColor,
  inletCount = 1,
  mixerState,
  levelSensorState,
  showScale = true,
  showDigital = true,
  className,
  style,
}) => {
  const uid = useId().replace(/:/g, '_');
  const clampedPercent = Math.max(0, Math.min(100, Number(levelPercent) || 0));
  const effectiveInletCount = Math.max(1, Math.min(4, Number(inletCount) || 1));
  const isMixerAttached = Boolean(mixerState?.isAttached);
  const isMixerRunning = Boolean(mixerState?.isRunning);
  const isSensorAttached = Boolean(levelSensorState?.isAttached);
  const mixerSpeed = Number(mixerState?.speed || 100);
  const mixerAnimDuration = isMixerRunning && mixerSpeed > 0 ? Math.max(0.15, 1.2 / (mixerSpeed / 100)) : 0;

  // Sight glass window geometry
  const windowTopY = 46;
  const windowBottomY = 138;
  const windowHeight = windowBottomY - windowTopY; // 92px
  const windowLeftX = 43;
  const windowWidth = 44;
  const windowCenterX = windowLeftX + windowWidth / 2; // 65

  // Liquid height based on percentage
  const liquidHeight = (clampedPercent / 100) * windowHeight;
  const liquidTopY = windowBottomY - liquidHeight;

  // Approximate current volume
  const currentVolume = Math.round((clampedPercent / 100) * capacity);

  return (
    <g className={className} style={style}>
      <defs>
        {/* Tank Body 3D Cylindrical Metallic Gradient */}
        <linearGradient id={`tankCylinder-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#1e293b" />
          <stop offset="12%" stopColor="#334155" />
          <stop offset="28%" stopColor="#94a3b8" />
          <stop offset="42%" stopColor="#cbd5e1" />
          <stop offset="55%" stopColor="#64748b" />
          <stop offset="78%" stopColor="#334155" />
          <stop offset="92%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Top Dome Curved Spherical Gradient */}
        <radialGradient id={`tankDome-${uid}`} cx="45%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#e2e8f0" />
          <stop offset="35%" stopColor="#94a3b8" />
          <stop offset="70%" stopColor="#475569" />
          <stop offset="100%" stopColor="#1e293b" />
        </radialGradient>

        {/* Bottom Dish Curved Gradient */}
        <linearGradient id={`tankBottomDish-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="50%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Vertical Pipe Gradient (Top Inlet) */}
        <linearGradient id={`pipeVertical-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#1e293b" />
          <stop offset="30%" stopColor="#94a3b8" />
          <stop offset="60%" stopColor="#475569" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Horizontal Pipe Gradient (Bottom Outlet) */}
        <linearGradient id={`pipeHorizontal-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#94a3b8" />
          <stop offset="30%" stopColor="#cbd5e1" />
          <stop offset="60%" stopColor="#475569" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        {/* Flange Collar Gradient */}
        <linearGradient id={`flangeGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#cbd5e1" />
          <stop offset="50%" stopColor="#64748b" />
          <stop offset="100%" stopColor="#334155" />
        </linearGradient>

        {/* Fluid 3D Deep Water Gradient */}
        <linearGradient id={`liquidGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={liquidColor || '#0369a1'} />
          <stop offset="25%" stopColor={liquidColor ? '#38bdf8' : '#38bdf8'} />
          <stop offset="55%" stopColor={liquidColor || '#0ea5e9'} />
          <stop offset="85%" stopColor={liquidColor || '#0284c7'} />
          <stop offset="100%" stopColor={liquidColor ? '#0369a1' : '#0369a1'} />
        </linearGradient>

        {/* Fluid Meniscus (Top Surface) Gradient */}
        <radialGradient id={`meniscusGrad-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#e0f2fe" stopOpacity="0.95" />
          <stop offset="45%" stopColor="#38bdf8" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#0284c7" stopOpacity="0.8" />
        </radialGradient>

        {/* Sight Glass Cavity & Glass Reflection Gradient */}
        <linearGradient id={`glassReflection-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(255, 255, 255, 0.4)" />
          <stop offset="25%" stopColor="rgba(255, 255, 255, 0.08)" />
          <stop offset="50%" stopColor="rgba(255, 255, 255, 0)" />
          <stop offset="80%" stopColor="rgba(255, 255, 255, 0.15)" />
          <stop offset="100%" stopColor="rgba(255, 255, 255, 0.35)" />
        </linearGradient>

        {/* Weld Seam Bevel */}
        <filter id={`weldGlow-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1" stdDeviation="0.8" floodColor="#000" floodOpacity="0.6" />
        </filter>

        {/* Digital Readout Glow */}
        <filter id={`digitalGlow-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="1.2" result="glow" />
          <feMerge>
            <feMergeNode in="glow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Clip path for Sight Glass Window */}
        <clipPath id={`sightWindowClip-${uid}`}>
          <rect x={windowLeftX + 2} y={windowTopY + 2} width={windowWidth - 4} height={windowHeight - 4} rx="4" />
        </clipPath>

        {/* Mixer Shaft & Blades Gradients */}
        <linearGradient id={`tankShaftGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="30%" stopColor="#cbd5e1" />
          <stop offset="55%" stopColor="#ffffff" />
          <stop offset="80%" stopColor="#94a3b8" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        <linearGradient id={`tankBladeGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="35%" stopColor="#cbd5e1" />
          <stop offset="65%" stopColor="#f8fafc" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>
      </defs>

      <style>{`
        @keyframes tank-bubble-rise-${uid} {
          0% { transform: translateY(0); opacity: 0.8; }
          50% { opacity: 0.4; }
          100% { transform: translateY(-40px); opacity: 0; }
        }
        @keyframes tank-mixer-spin-${uid} {
          0% { transform: scaleX(1); }
          25% { transform: scaleX(0.2); }
          50% { transform: scaleX(-1); }
          75% { transform: scaleX(-0.2); }
          100% { transform: scaleX(1); }
        }
        .tank-bubble-${uid} {
          animation: tank-bubble-rise-${uid} 2.5s infinite linear;
        }
      `}</style>

      {/* Ground Cast Shadow */}
      <ellipse cx="65" cy="176" rx="46" ry="4" fill="rgba(0, 0, 0, 0.45)" />

      {/* --- SUPPORT LEGS (Tank Ayakları) --- */}
      {/* Left Leg */}
      <g>
        <rect x="29" y="152" width="10" height="23" fill="#1e293b" stroke="#0f172a" strokeWidth="1" />
        <rect x="30" y="152" width="4" height="23" fill="#475569" />
        {/* Foot Base Plate & Anchor Bolt */}
        <rect x="26" y="173" width="16" height="4" rx="1" fill="#334155" stroke="#0f172a" strokeWidth="0.8" />
        <circle cx="30" cy="175" r="1.2" fill="#cbd5e1" />
        <circle cx="38" cy="175" r="1.2" fill="#cbd5e1" />
      </g>

      {/* Right Leg */}
      <g>
        <rect x="91" y="152" width="10" height="23" fill="#1e293b" stroke="#0f172a" strokeWidth="1" />
        <rect x="92" y="152" width="4" height="23" fill="#475569" />
        {/* Foot Base Plate & Anchor Bolt */}
        <rect x="88" y="173" width="16" height="4" rx="1" fill="#334155" stroke="#0f172a" strokeWidth="0.8" />
        <circle cx="92" cy="175" r="1.2" fill="#cbd5e1" />
        <circle cx="100" cy="175" r="1.2" fill="#cbd5e1" />
      </g>

      {/* --- PIPE INLETS (Boru Girişleri) --- */}
      {effectiveInletCount <= 1 ? (
        /* Single Top Pipe Inlet (Tekli Üst Boru Girişi) */
        <g data-testid="tank-top-inlet">
          {/* Pipe Neck */}
          <rect
            x="60"
            y="9"
            width="10"
            height="18"
            fill={`url(#pipeVertical-${uid})`}
            stroke="#0f172a"
            strokeWidth="0.9"
          />
          {/* Pipe Flange Collar */}
          <rect
            x="57"
            y="4"
            width="16"
            height="5"
            rx="1.5"
            fill={`url(#flangeGrad-${uid})`}
            stroke="#0f172a"
            strokeWidth="0.9"
          />
          {/* Flange Bolts */}
          <circle cx="59" cy="6.5" r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
          <circle cx="63" cy="6.5" r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
          <circle cx="67" cy="6.5" r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
          <circle cx="71" cy="6.5" r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
          {/* Pipe Port Opening Orifice (Inlet Connection Port) */}
          <ellipse cx="65" cy="4.5" rx="5" ry="1.6" fill="#090d16" stroke="#475569" strokeWidth="0.7" />
          <ellipse cx="65" cy="4.5" rx="3.5" ry="1.1" fill="#020617" />
        </g>
      ) : (
        /* Multiple Inlets: Left Side Nozzles & Top Breather Cap */
        <>
          {/* Top Breather Vent Filter (Üst Havalandırma Filtresi) */}
          <g data-testid="tank-top-breather">
            <rect x="62" y="18" width="6" height="9" fill="#334155" stroke="#0f172a" strokeWidth="0.8" />
            <ellipse cx="65" cy="18" rx="8" ry="3.5" fill={`url(#flangeGrad-${uid})`} stroke="#0f172a" strokeWidth="0.8" />
            <path d="M 57 18 Q 65 11 73 18 Z" fill={`url(#flangeGrad-${uid})`} stroke="#0f172a" strokeWidth="0.8" />
            <circle cx="65" cy="14" r="1.5" fill="#f8fafc" />
          </g>

          {/* Multiple Left Side Inlets */}
          {Array.from({ length: effectiveInletCount }, (_, i) => {
            const inletY = getLeftInletY(i, effectiveInletCount);
            return (
              <g key={`left-inlet-${i}`} data-testid={`tank-left-inlet-${i}`}>
                {/* Horizontal Pipe Extension (Left Side) */}
                <rect
                  x="6"
                  y={inletY - 4}
                  width="18"
                  height="8"
                  fill={`url(#pipeHorizontal-${uid})`}
                  stroke="#0f172a"
                  strokeWidth="0.8"
                />
                {/* Pipe Flange Collar */}
                <rect
                  x="4"
                  y={inletY - 6.5}
                  width="4.5"
                  height="13"
                  rx="1.5"
                  fill={`url(#flangeGrad-${uid})`}
                  stroke="#0f172a"
                  strokeWidth="0.8"
                />
                {/* Flange Bolts */}
                <circle cx="6.25" cy={inletY - 4} r="0.8" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
                <circle cx="6.25" cy={inletY + 4} r="0.8" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
                {/* Pipe Port Opening Orifice (Inlet Connection Port) */}
                <ellipse cx="4" cy={inletY} rx="1.4" ry="4.2" fill="#090d16" stroke="#475569" strokeWidth="0.7" />
                <ellipse cx="4" cy={inletY} rx="0.9" ry="2.8" fill="#020617" />
                {/* Welded Seam at tank body */}
                <line x1="23" y1={inletY - 5} x2="23" y2={inletY + 5} stroke="#0f172a" strokeWidth="1" />
              </g>
            );
          })}
        </>
      )}

      {/* --- BOTTOM PIPE OUTLET (Alt Boru Çıkışı) --- */}
      {/* Horizontal Pipe Extension (Right Side) */}
      <path
        d="M 103 144 L 131 144 L 131 152 L 103 152 Z"
        fill={`url(#pipeHorizontal-${uid})`}
        stroke="#0f172a"
        strokeWidth="0.8"
      />
      {/* Pipe Flange Collar (Right Side Vertical Flange) */}
      <rect
        x="130"
        y="140.5"
        width="4.5"
        height="15"
        rx="1.5"
        fill={`url(#flangeGrad-${uid})`}
        stroke="#0f172a"
        strokeWidth="0.8"
      />
      {/* Flange Bolts */}
      <circle cx="132.25" cy={142.5} r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
      <circle cx="132.25" cy={146} r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
      <circle cx="132.25" cy={150} r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
      <circle cx="132.25" cy={153.5} r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
      {/* Pipe Port Opening Orifice (Outlet Connection Port) */}
      <ellipse cx="136" cy={148} rx="1.4" ry="4.5" fill="#090d16" stroke="#475569" strokeWidth="0.7" />
      <ellipse cx="136" cy={148} rx="0.9" ry="3" fill="#020617" />

      {/* --- TANK MAIN BODY (3D Silindirik Gövde) --- */}
      {/* Bottom Dish (Alt Bombe) */}
      <path
        d="M 23 144 Q 65 162 107 144 L 107 140 Q 65 156 23 140 Z"
        fill={`url(#tankBottomDish-${uid})`}
        stroke="#0f172a"
        strokeWidth="1"
      />

      {/* Main Cylinder Body */}
      <rect
        x="23"
        y="36"
        width="84"
        height="108"
        fill={`url(#tankCylinder-${uid})`}
        stroke="#0f172a"
        strokeWidth="1.2"
      />

      {/* Top Dome Cap (Üst Bombe / Kubbe) */}
      <ellipse
        cx="65"
        cy="36"
        rx="42"
        ry="13"
        fill={`url(#tankDome-${uid})`}
        stroke="#0f172a"
        strokeWidth="1"
      />

      {/* Top Dome Specular Arc Highlight */}
      <path
        d="M 33 34 Q 65 25 97 34"
        fill="none"
        stroke="rgba(255, 255, 255, 0.45)"
        strokeWidth="2"
        strokeLinecap="round"
      />

      {/* Welded Seams / Horizontal Ribs with 3D Depth */}
      <g filter={`url(#weldGlow-${uid})`}>
        {/* Upper Weld Ring */}
        <line x1="23" y1="42" x2="107" y2="42" stroke="#0f172a" strokeWidth="1.5" />
        <line x1="23" y1="42.8" x2="107" y2="42.8" stroke="rgba(255, 255, 255, 0.35)" strokeWidth="0.8" />

        {/* Lower Weld Ring */}
        <line x1="23" y1="143" x2="107" y2="143" stroke="#0f172a" strokeWidth="1.5" />
        <line x1="23" y1="143.8" x2="107" y2="143.8" stroke="rgba(255, 255, 255, 0.35)" strokeWidth="0.8" />
      </g>

      {/* Top Inspection Manhole or Mixer Mounting Flange Coupling */}
      {isMixerAttached ? (
        <g data-testid="tank-mixer-mounting-flange">
          {/* Heavy-duty Mixer Base Flange & Bolts */}
          <ellipse cx="65" cy="24" rx="16" ry="5.5" fill={`url(#flangeGrad-${uid})`} stroke="#0f172a" strokeWidth="1" />
          <ellipse cx="65" cy="23.5" rx="12" ry="4" fill="#334155" />
          <circle cx="53" cy="24" r="1.1" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
          <circle cx="59" cy="22" r="1.1" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
          <circle cx="71" cy="22" r="1.1" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
          <circle cx="77" cy="24" r="1.1" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
          {/* Shaft Seal / Gland Collar */}
          <rect x="61" y="16" width="8" height="8" rx="1" fill="#1e293b" stroke="#0f172a" strokeWidth="0.8" />
          <ellipse cx="65" cy="16" rx="4" ry="1.5" fill="#475569" stroke="#0f172a" strokeWidth="0.5" />
        </g>
      ) : (
        /* Top Inspection Manhole / Hatch (Üst Menhol Kapağı) */
        <g data-testid="tank-manhole-hatch">
          <ellipse cx="65" cy="24" rx="14" ry="4.5" fill="#334155" stroke="#0f172a" strokeWidth="1" />
          <ellipse cx="65" cy="23.5" rx="11" ry="3.5" fill="#475569" />
          <circle cx="56" cy="24" r="0.9" fill="#cbd5e1" />
          <circle cx="65" cy="22" r="0.9" fill="#cbd5e1" />
          <circle cx="74" cy="24" r="0.9" fill="#cbd5e1" />
          <circle cx="65" cy="26" r="0.9" fill="#cbd5e1" />
        </g>
      )}

      {/* Top Right Level Sensor Nozzle / Flange */}
      {isSensorAttached && (
        <g data-testid="tank-sensor-mounting-flange">
          <ellipse cx="94" cy="27" rx="6" ry="2.2" fill={`url(#flangeGrad-${uid})`} stroke="#0f172a" strokeWidth="0.8" />
          <ellipse cx="94" cy="26.5" rx="4.5" ry="1.6" fill="#334155" />
          <circle cx="91" cy="27" r="0.6" fill="#f8fafc" />
          <circle cx="97" cy="27" r="0.6" fill="#f8fafc" />
          <rect x="92" y="21" width="4" height="6" fill="#1e293b" stroke="#0f172a" strokeWidth="0.6" />
        </g>
      )}

      {/* --- SIGHT GLASS / LEVEL GAUGE (Gözetleme Camı & Sıvı Seviyesi) --- */}
      {/* Outer Beveled Industrial Frame */}
      <rect
        x={windowLeftX}
        y={windowTopY}
        width={windowWidth}
        height={windowHeight}
        rx="6"
        fill="#0b0f19"
        stroke="#1e293b"
        strokeWidth="2.5"
      />
      {/* Frame Mounting Screws */}
      <circle cx={windowLeftX + 3.5} cy={windowTopY + 4} r="1" fill="#64748b" />
      <circle cx={windowLeftX + windowWidth - 3.5} cy={windowTopY + 4} r="1" fill="#64748b" />
      <circle cx={windowLeftX + 3.5} cy={windowBottomY - 4} r="1" fill="#64748b" />
      <circle cx={windowLeftX + windowWidth - 3.5} cy={windowBottomY - 4} r="1" fill="#64748b" />

      {/* Inner Cavity (Dark background behind fluid) */}
      <rect
        x={windowLeftX + 2}
        y={windowTopY + 2}
        width={windowWidth - 4}
        height={windowHeight - 4}
        rx="4"
        fill="#050811"
      />

      {/* --- LIQUID FILL & DYNAMICS (Sıvı Doldurma Alanı) --- */}
      <g clipPath={`url(#sightWindowClip-${uid})`}>
        {/* MIXER SHAFT & IMPELLER INSIDE TANK */}
        {isMixerAttached && (
          <g data-testid="tank-internal-mixer">
            {/* Rotating Mixer Shaft */}
            <rect
              x="63.5"
              y="38"
              width="3"
              height="88"
              fill={`url(#tankShaftGrad-${uid})`}
              stroke="#0f172a"
              strokeWidth="0.6"
            />
            {/* Shaft specular highlight */}
            <line x1="64.8" y1="40" x2="64.8" y2="124" stroke="rgba(255, 255, 255, 0.65)" strokeWidth="0.7" />

            {/* Upper Dispersion Agitator Blade (at y=82) */}
            <g
              data-testid="tank-mixer-upper-blade"
              style={{
                transformOrigin: '65px 82px',
                animation: isMixerRunning ? `tank-mixer-spin-${uid} ${mixerAnimDuration}s infinite linear` : undefined,
              }}
            >
              <path
                d="M 63.5 81 C 56 79 52 82 51 85 C 55 86 61 84 63.5 83 Z"
                fill={`url(#tankBladeGrad-${uid})`}
                stroke="#0f172a"
                strokeWidth="0.6"
              />
              <path
                d="M 66.5 81 C 74 79 78 82 79 85 C 75 86 69 84 66.5 83 Z"
                fill={`url(#tankBladeGrad-${uid})`}
                stroke="#0f172a"
                strokeWidth="0.6"
              />
              <circle cx="65" cy="82" r="2.8" fill="#334155" stroke="#0f172a" strokeWidth="0.5" />
            </g>

            {/* Lower Mixing Impeller / Turbine (at y=122) */}
            <g
              data-testid="tank-mixer-lower-blade"
              style={{
                transformOrigin: '65px 122px',
                animation: isMixerRunning ? `tank-mixer-spin-${uid} ${mixerAnimDuration}s infinite linear` : undefined,
              }}
            >
              {mixerState?.bladeType === 'anchor' ? (
                <path
                  d="M 50 110 L 50 125 Q 65 133 80 125 L 80 110 L 77 110 L 77 123 Q 65 129 53 123 L 53 110 Z"
                  fill={`url(#tankBladeGrad-${uid})`}
                  stroke="#0f172a"
                  strokeWidth="0.7"
                />
              ) : mixerState?.bladeType === 'turbine' ? (
                <g>
                  <ellipse cx="65" cy="122" rx="14" ry="3.5" fill="#475569" stroke="#0f172a" strokeWidth="0.6" />
                  <rect x="52" y="118" width="3" height="8" fill={`url(#tankBladeGrad-${uid})`} stroke="#0f172a" strokeWidth="0.5" />
                  <rect x="63.5" y="118" width="3" height="8" fill={`url(#tankBladeGrad-${uid})`} stroke="#0f172a" strokeWidth="0.5" />
                  <rect x="75" y="118" width="3" height="8" fill={`url(#tankBladeGrad-${uid})`} stroke="#0f172a" strokeWidth="0.5" />
                </g>
              ) : (
                <g>
                  <path
                    d="M 63.5 121 C 53 117 46 122 45 126 C 51 128 59 125 63.5 123 Z"
                    fill={`url(#tankBladeGrad-${uid})`}
                    stroke="#0f172a"
                    strokeWidth="0.7"
                  />
                  <path
                    d="M 66.5 121 C 77 117 84 122 85 126 C 79 128 71 125 66.5 123 Z"
                    fill={`url(#tankBladeGrad-${uid})`}
                    stroke="#0f172a"
                    strokeWidth="0.7"
                  />
                </g>
              )}
              {/* Impeller End Nut */}
              <circle cx="65" cy="122" r="3.2" fill="#1e293b" stroke="#0f172a" strokeWidth="0.5" />
              <circle cx="65" cy="122" r="1.4" fill="#cbd5e1" />
            </g>
          </g>
        )}

        {/* Tank Internal Level Sensor Immersion Probe & Float */}
        {isSensorAttached && (
          <g data-testid="tank-internal-sensor-probe">
            {/* Stainless Steel Probe Rod */}
            <rect
              x="81"
              y="44"
              width="2"
              height={windowHeight}
              fill={`url(#tankShaftGrad-${uid})`}
              stroke="#0f172a"
              strokeWidth="0.5"
            />
            <line x1="82" y1="44" x2="82" y2={windowBottomY} stroke="rgba(255, 255, 255, 0.6)" strokeWidth="0.4" />
            {/* Magnetic Float tracking the liquid level surface */}
            <g
              data-testid="tank-internal-sensor-float"
              style={{
                transform: `translateY(${liquidTopY - windowBottomY}px)`,
                transformOrigin: `82px ${windowBottomY}px`,
                transition: 'transform 0.25s ease-out',
              }}
            >
              <rect x="78" y={windowBottomY - 2.5} width="8" height="5" rx="1.2" fill="#f8fafc" stroke="#0f172a" strokeWidth="0.5" />
              <line x1="78" y1={windowBottomY} x2="86" y2={windowBottomY} stroke="#334155" strokeWidth="0.4" />
            </g>
          </g>
        )}

        {/* Liquid Column */}
        {liquidHeight > 0 && (
          <g>
            {/* Liquid Main Body (with slight transparency so submerged shaft is visible) */}
            <rect
              x={windowLeftX + 2}
              y={liquidTopY}
              width={windowWidth - 4}
              height={liquidHeight + 4}
              fill={`url(#liquidGrad-${uid})`}
              fillOpacity={isMixerAttached ? 0.8 : 1}
            />

            {/* Micro Rising Bubble Details */}
            {clampedPercent > 5 && (
              <g className={`tank-bubble-${uid}`}>
                <circle cx={windowCenterX - 10} cy={windowBottomY - 15} r="1.5" fill="#e0f2fe" opacity="0.6" />
                <circle cx={windowCenterX + 8} cy={windowBottomY - 30} r="1.8" fill="#e0f2fe" opacity="0.5" />
                <circle cx={windowCenterX - 3} cy={windowBottomY - 45} r="1.2" fill="#e0f2fe" opacity="0.7" />
              </g>
            )}

            {/* Top Surface Meniscus / Agitated Vortex */}
            {isMixerRunning ? (
              /* Dynamic Agitation Vortex Whirlpool Surface */
              <path
                data-testid="tank-vortex-surface"
                d={`M ${windowLeftX + 2} ${liquidTopY} Q ${windowCenterX - 10} ${liquidTopY - 1} ${windowCenterX} ${liquidTopY + 4} Q ${windowCenterX + 10} ${liquidTopY - 1} ${windowLeftX + windowWidth - 2} ${liquidTopY} Z`}
                fill={`url(#meniscusGrad-${uid})`}
                stroke="rgba(255, 255, 255, 0.85)"
                strokeWidth="1"
              />
            ) : (
              /* Calm Fluid Meniscus */
              <ellipse
                cx={windowCenterX}
                cy={liquidTopY}
                rx={(windowWidth - 6) / 2}
                ry="3"
                fill={`url(#meniscusGrad-${uid})`}
                stroke="rgba(255, 255, 255, 0.7)"
                strokeWidth="0.8"
              />
            )}
          </g>
        )}

        {/* Glass Front Gloss / Specular Highlights */}
        <rect
          x={windowLeftX + 2}
          y={windowTopY + 2}
          width={windowWidth - 4}
          height={windowHeight - 4}
          fill={`url(#glassReflection-${uid})`}
          pointerEvents="none"
        />

        {/* Vertical Specular Glass Stripe */}
        <rect
          x={windowLeftX + 6}
          y={windowTopY + 2}
          width="4"
          height={windowHeight - 4}
          fill="rgba(255, 255, 255, 0.2)"
          pointerEvents="none"
        />
      </g>

      {/* --- MEASUREMENT SCALE (Seviye Cetveli & Skala) --- */}
      {showScale && (
        <g opacity="0.85">
          {/* 100% Mark */}
          <line x1={windowLeftX + windowWidth - 7} y1={windowTopY + 4} x2={windowLeftX + windowWidth - 2} y2={windowTopY + 4} stroke="#94a3b8" strokeWidth="1" />
          <text x={windowLeftX + windowWidth + 2} y={windowTopY + 6} fill="#cbd5e1" fontSize="5.5" fontFamily="monospace" fontWeight="bold">100</text>

          {/* 75% Mark */}
          <line x1={windowLeftX + windowWidth - 6} y1={windowTopY + windowHeight * 0.25} x2={windowLeftX + windowWidth - 2} y2={windowTopY + windowHeight * 0.25} stroke="#94a3b8" strokeWidth="0.8" />
          <text x={windowLeftX + windowWidth + 2} y={windowTopY + windowHeight * 0.25 + 2} fill="#94a3b8" fontSize="5" fontFamily="monospace">75</text>

          {/* 50% Mark */}
          <line x1={windowLeftX + windowWidth - 7} y1={windowTopY + windowHeight * 0.5} x2={windowLeftX + windowWidth - 2} y2={windowTopY + windowHeight * 0.5} stroke="#94a3b8" strokeWidth="1" />
          <text x={windowLeftX + windowWidth + 2} y={windowTopY + windowHeight * 0.5 + 2} fill="#cbd5e1" fontSize="5" fontFamily="monospace" fontWeight="bold">50</text>

          {/* 25% Mark */}
          <line x1={windowLeftX + windowWidth - 6} y1={windowTopY + windowHeight * 0.75} x2={windowLeftX + windowWidth - 2} y2={windowTopY + windowHeight * 0.75} stroke="#94a3b8" strokeWidth="0.8" />
          <text x={windowLeftX + windowWidth + 2} y={windowTopY + windowHeight * 0.75 + 2} fill="#94a3b8" fontSize="5" fontFamily="monospace">25</text>

          {/* 0% Mark */}
          <line x1={windowLeftX + windowWidth - 7} y1={windowBottomY - 4} x2={windowLeftX + windowWidth - 2} y2={windowBottomY - 4} stroke="#94a3b8" strokeWidth="1" />
          <text x={windowLeftX + windowWidth + 2} y={windowBottomY - 2} fill="#cbd5e1" fontSize="5.5" fontFamily="monospace" fontWeight="bold">0</text>
        </g>
      )}

      {/* --- DIGITAL READOUT PANEL (Dijital Seviye Göstergesi) --- */}
      {showDigital && (
        <g>
          {/* LCD Bezel Frame */}
          <rect
            x={windowCenterX - 22}
            y={windowTopY - 14}
            width="44"
            height="13"
            rx="2.5"
            fill="#090d16"
            stroke="#334155"
            strokeWidth="1.2"
          />
          {/* LED Glow Indicator Dot (Green/Blue normal, Red high alert, Amber low) */}
          <circle
            cx={windowCenterX - 17}
            cy={windowTopY - 7.5}
            r="1.8"
            fill={clampedPercent >= 90 ? '#ef4444' : (clampedPercent <= 10 ? '#f59e0b' : '#10b981')}
            filter={`url(#digitalGlow-${uid})`}
          />
          {/* Numerical Level Text */}
          <text
            x={windowCenterX + 3}
            y={windowTopY - 4.5}
            fill="#38bdf8"
            fontSize="8"
            fontFamily="monospace"
            fontWeight="bold"
            textAnchor="middle"
            filter={`url(#digitalGlow-${uid})`}
          >
            {Math.round(clampedPercent)}%
          </text>
        </g>
      )}

      {/* Bottom Capacity / Unit Badge */}
      <text
        x={windowCenterX}
        y={150}
        fill="#64748b"
        fontSize="6"
        fontFamily="sans-serif"
        fontWeight="600"
        textAnchor="middle"
        letterSpacing="0.5"
      >
        {currentVolume} / {capacity} L
      </text>

      {/* Pipe Connection Port Anchors (Visual Flange Marks for Alignment) */}
      {/* Top Inlet Port Marker (65, 4.5) */}
      <circle cx="65" cy="4.5" r="1.5" fill="#38bdf8" opacity="0.4" />
      {/* Bottom Outlet Port Marker (136, 148) */}
      <circle cx="136" cy="148" r="1.5" fill="#38bdf8" opacity="0.4" />
    </g>
  );
};
